import React, { useCallback, useState } from 'react'
import cn from 'classnames'
import styled from '@emotion/styled'
import { CSSTransition } from 'react-transition-group'

import EmptyMessage from 'components/App/DesktopView/Layout/RightSidebar/InAppCards/shared/EmptyMessage'
import AnimateAutoHeight from 'components/AnimateAutoHeight'
import CollapsableCard from 'components/CollapsableCard'
import SortableVisibleControlsWithTitle from 'components/crm/ContactDetailsColumn/SortableVisibleControlsWithTitle'
import themeVars from 'ui_theme/site/globals/site.variables'
import { Label, Loader } from 'shared/ui'

import Detail from './Detail'
import List from './List'
import ListItem from './ListItem'

const ANIMATION_DURATION = 300

const styledComponent = styled(NavigatableIntegrationWidgetCard)`
  .NavigatableIntegrationWidgetCard-Detail-icon {
    font-size: ${props => props.theme.spacing['6']};
    color: ${props => props.theme.color.monochrome.mediumDark};
    cursor: pointer;
    margin-right: ${props => props.theme.spacing['5']};
    line-height: ${props => props.theme.spacing['7']};
  }

  a .NavigatableIntegrationWidgetCard-Detail-icon {
    color: inherit;
  }

  .NavigatableIntegrationWidgetCard-Detail-title {
    font-size: ${props => props.theme.spacing['7']};
    font-weight: ${themeVars.graphikWeightMedium};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .NavigatableIntegrationWidgetCard-List {
    margin: ${props => props.theme.spacing['5']} -${props =>
  props.theme.spacing['7']} ${themeVars.small};
    ${Loader} {
      margin-top: ${props => props.theme.spacing['5']};
    }
  }

  .NavigatableIntegrationWidgetCard-ListItem {
    border-bottom: 1px solid ${props => props.theme.color.monochrome.medium};
    padding: 6px ${props => props.theme.spacing['7']} 9px;
    cursor: pointer;
    display: flex;
    align-items: center;

    &:hover {
      background: #e3f6fe;
    }
  }

  .NavigatableIntegrationWidgetCard-ListItem-iconColumn {
    flex-grow: 0;
    flex-shrink: 0;

    .icon {
      font-size: ${props => props.theme.spacing['6']};
      color: ${props => props.theme.color.monochrome.mediumDark};
    }
  }
  .NavigatableIntegrationWidgetCard-ListItem-titleAndSubTitleColumn {
    flex-grow: 1;
    min-width: 0;
    .NavigatableIntegrationWidgetCard-ListItem-title,
    .NavigatableIntegrationWidgetCard-ListItem-subTitle {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      line-height: 1.2em;
    }

    .NavigatableIntegrationWidgetCard-ListItem-title {
      color: ${props => props.theme.color.primary.brand};
      font-size: ${props => props.theme.spacing.base};
      line-height: ${props => props.theme.spacing['11']};
    }

    .NavigatableIntegrationWidgetCard-ListItem-subtTitle {
      font-size: ${props => props.theme.spacing['7']};
      line-height: ${props => props.theme.spacing['10']};
    }
  }

  ${Label} {
    display: block;
    margin-top: 16px;
    &:first-of-type {
      margin-top: 0;
    }
  }
  ${Label} + ${EmptyMessage} {
    margin-top: 8px;
  }

  .ui.tabular.menu {
    max-width: none;
    width: auto !important;
    border-top: 1px solid ${props => props.theme.color.monochrome.medium};
    border-bottom: 1px solid ${props => props.theme.color.monochrome.medium};
    border-radius: 0;
    margin-bottom: 0;
    justify-content: center;
    .item, .item.active {
      margin-top: 0;
      margin-bottom: 0;
      border-radius: 0 !important;
      border-top: 0;
      border-right: 1px solid transparent;
      border-bottom: 0;
      border-left: 1px solid ${props => props.theme.color.monochrome.medium};
      flex: 1;
      justify-content: center;
      &:first-of-type {
        border-left: 1px solid transparent;
      }
    }
    .item.active {
      font-weight: ${props => props.theme.fontWeight.medium};
    }
  }

`
export default styledComponent

styledComponent.Detail = Detail
styledComponent.List = List
styledComponent.ListItem = ListItem

function NavigatableIntegrationWidgetCard(props) {
  const {
    detailComponent: DetailComponent,
    doHideCard,
    className,
    image,
    indexComponent: IndexComponent,
    persistenceKey,
    title,
    unmountOnExit = true,
    refreshable = false,
    onRefresh,
    isLoading,
    hidePadding = false,
  } = props
  const [state, setState] = useState(null)
  const [deferedState, setDeferedState] = useState(null)
  const [isInTransition, doSetIsInTransition] = useState(false)
  const setStateAndDeferedState = useCallback(
    newState => {
      setState(newState)
      const timeout = setTimeout(
        () => setDeferedState(newState),
        ANIMATION_DURATION
      )
      return () => clearTimeout(timeout)
    },
    [setState, setDeferedState]
  )
  const setStateNow = useCallback(
    newState => {
      setState(newState)
      setDeferedState(newState)
    },
    [setState, setDeferedState]
  )
  const doNavigatableClearState = useCallback(
    () => setStateAndDeferedState(null),
    [setStateAndDeferedState]
  )
  const onTransitioning = useCallback(() => doSetIsInTransition(true), [
    doSetIsInTransition,
  ])
  const onTransitioned = useCallback(() => doSetIsInTransition(false), [
    doSetIsInTransition,
  ])

  return (
    <CollapsableCard
      className={cn('NavigatableIntegrationWidgetCard', className)}
      hideOverflow={isInTransition}
      hidePadding={hidePadding}
      image={image}
      persistenceKey={persistenceKey}
      isLoading={isLoading}
      title={
        <SortableVisibleControlsWithTitle
          title={title}
          onHide={doHideCard}
          refreshable={refreshable}
          onRefresh={onRefresh}
        />
      }
    >
      <AnimateAutoHeight duration={ANIMATION_DURATION}>
        <AnimatedLeftRight>
          <CSSTransition
            className="index"
            classNames="index"
            in={state === null}
            onEntering={onTransitioning}
            onEntered={onTransitioned}
            onExiting={onTransitioning}
            onExited={onTransitioned}
            timeout={ANIMATION_DURATION}
            unmountOnExit={unmountOnExit}
          >
            <div>
              <IndexComponent
                doNavigatableClearState={doNavigatableClearState}
                doNavigatableSetState={setStateNow}
                navigatableState={deferedState}
                {...props}
              />
            </div>
          </CSSTransition>
          <CSSTransition
            className="detail"
            classNames="detail"
            in={state !== null}
            unmountOnExit={unmountOnExit}
            timeout={ANIMATION_DURATION}
          >
            <div>
              <DetailComponent
                doNavigatableClearState={doNavigatableClearState}
                doNavigatableSetState={setStateNow}
                navigatableState={deferedState}
                {...props}
              />
            </div>
          </CSSTransition>
        </AnimatedLeftRight>
      </AnimateAutoHeight>
    </CollapsableCard>
  )
}

const AnimatedLeftRight = styled('div')`
  display: flex;

  .index,
  .detail {
    flex-shrink: 0;
    flex-grow: 0;
    flex-basis: 100%;
    transition: transform ease-in-out ${ANIMATION_DURATION}ms;
    min-width: 0;
  }

  .detail,
  .detail-enter {
    transform: translateX(calc(100% + 16px));
  }

  .index {
    margin-right: -100%;
  }

  .index-exit,
  .index-enter,
  .index-exit-done {
    transform: translateX(calc(-100% - 16px));
  }

  .index-exit-done,
  .detail {
    display: none;
  }

  .detail-enter,
  .detail-enter-done,
  .detail-exit {
    display: block;
  }

  .index-enter-active,
  .index-enter-done {
    transform: translateX(0);
  }

  .detail-enter-active,
  .detail-enter-done {
    transform: translateX(0);
  }

  .detail-exit-active,
  .detail-exit-done,
  .index-exit-active,
  .index-exit-done {
    height: 0;
  }
`
